import type { FC, ReactNode } from "react";

// ---------------------------------------------------------------------------
// Shared retro-terminal primitives for the blackboard surface.
// Use these instead of re-inlining corner brackets, [ BRACKET ] labels, and
// Pill buttons. Each tab that adopts them stays visually coherent with the
// BurnRateModal + InstanceDrawerHeader without duplicating chrome code.
// ---------------------------------------------------------------------------

const BRACKET_POS: Record<BracketPos, string> = {
  bl: "bottom-1 left-1 border-b border-l",
  br: "bottom-1 right-1 border-b border-r",
  tl: "top-1 left-1 border-l border-t",
  tr: "top-1 right-1 border-r border-t",
};

type BracketPos = "tl" | "tr" | "bl" | "br";

export const CornerBracket: FC<{
  position: BracketPos;
  /** Hex or CSS color; defaults to emerald-400/60 via Tailwind class if omitted. */
  color?: string;
  size?: "sm" | "md";
}> = ({ position, color, size = "sm" }) => {
  const sizeCls = size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2";
  const baseCls = `pointer-events-none absolute ${sizeCls} ${BRACKET_POS[position]}`;
  if (color) {
    return (
      <span aria-hidden className={baseCls} style={{ borderColor: color }} />
    );
  }
  return <span aria-hidden className={`${baseCls} border-emerald-400/50`} />;
};

export const CornerBrackets: FC<{ color?: string; size?: "sm" | "md" }> = ({
  color,
  size,
}) => (
  <>
    <CornerBracket position="tl" color={color} size={size} />
    <CornerBracket position="tr" color={color} size={size} />
    <CornerBracket position="bl" color={color} size={size} />
    <CornerBracket position="br" color={color} size={size} />
  </>
);

export const SectionLabel: FC<{
  label: string;
  meta?: ReactNode;
  accent?: "emerald" | "violet" | "sky" | "amber" | "rose" | "slate";
  className?: string;
}> = ({ label, meta, accent = "emerald", className = "" }) => {
  const accentMap = {
    amber:
      "text-amber-700/90 border-amber-500/30 dark:text-amber-300/80 dark:border-amber-400/15",
    emerald:
      "text-emerald-700/90 border-emerald-500/30 dark:text-emerald-300/80 dark:border-emerald-400/15",
    rose: "text-rose-700/90 border-rose-500/30 dark:text-rose-300/80 dark:border-rose-400/15",
    sky: "text-sky-700/90 border-sky-500/30 dark:text-sky-300/80 dark:border-sky-400/15",
    slate:
      "text-slate-600 border-slate-400/40 dark:text-slate-400 dark:border-slate-500/20",
    violet:
      "text-violet-700/90 border-violet-500/30 dark:text-violet-300/80 dark:border-violet-400/15",
  }[accent];
  return (
    <div
      className={`flex items-baseline justify-between border-b pb-1.5 ${accentMap} ${className}`}
    >
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]">
        [ {label} ]
      </p>
      {meta && (
        <p className="font-mono text-[10px] tracking-tight text-slate-500">
          {meta}
        </p>
      )}
    </div>
  );
};

export const PillButton: FC<{
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  accent?: "emerald" | "violet" | "amber" | "sky";
  type?: "button" | "submit";
  disabled?: boolean;
  title?: string;
}> = ({
  active,
  onClick,
  children,
  accent = "emerald",
  type = "button",
  disabled,
  title,
}) => {
  const activeMap = {
    amber:
      "border-amber-500/60 bg-amber-400/10 text-amber-800 dark:border-amber-400/60 dark:text-amber-200",
    emerald:
      "border-emerald-500/60 bg-emerald-400/10 text-emerald-800 dark:border-emerald-400/60 dark:text-emerald-200",
    sky: "border-sky-500/60 bg-sky-400/10 text-sky-800 dark:border-sky-400/60 dark:text-sky-200",
    violet:
      "border-violet-500/60 bg-violet-400/10 text-violet-800 dark:border-violet-400/60 dark:text-violet-200",
  }[accent];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40 ${
        active
          ? activeMap
          : "border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 dark:border-slate-700/80 dark:hover:border-slate-600 dark:hover:text-slate-300"
      }`}
    >
      {children}
    </button>
  );
};

export const RetroEmpty: FC<{
  label: string;
  sub?: string;
  /** Optional call-to-action rendered below the copy (e.g. a "New Room" button)
   *  so empty states can offer the next step instead of being dead ends. */
  action?: ReactNode;
}> = ({ label, sub, action }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 py-12 text-center">
    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
      [ {label} ]
    </p>
    {sub && (
      <p className="font-mono text-[10px] tracking-tight text-slate-500 dark:text-slate-600">
        {sub}
      </p>
    )}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const RetroError: FC<{ message: string }> = ({ message }) => (
  <div className="border border-rose-500/30 bg-rose-500/5 px-3 py-2">
    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-rose-700 dark:text-rose-300">
      [ ERROR ]
    </p>
    <p className="mt-0.5 font-mono text-[11px] text-rose-800/90 dark:text-rose-200/80">
      {message}
    </p>
  </div>
);
